import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    FaArrowLeft,
    FaHistory,
    FaSearch,
    FaFilter,
    FaArrowRight,
} from "react-icons/fa";
import FullPageSpinner from "../../../components/FullPageSpinner";
import { MenuOption, Table, TableColumn, TableMenu } from "../../../components/Table";
import { sendRequest } from "../../../utils/api";
import { FilterChip, FilterMenuItem, FilterOption, getDateLabel } from "../../../components/FilterComponents";
import { capitalizeFirst, capitalizeWords } from "../../../utils/string";
import AttendanceHistory from "../../../models/AttendanceHistory";
import { formatDate, formatTime } from "../../../utils/date";
import Site from "../../../models/Site";


export default function AttendanceHistoryPage() {
    const navigate = useNavigate();

    const [loading, setLoading] = useState(false);
    const [attendanceHistory, setAttendanceHistory] = useState<
        AttendanceHistory[]
    >([]);
    const [sites, setSites] = useState<Site[]>([])

    const [filter, setFilter] = useState({
        date: "", department: "all",
        site: "all", status: "all",
    });

    const [showFilters, setShowFilters] = useState(false);
    const [activeFilterMenu, setActiveFilterMenu] = useState<string | null>(null);

    const [search, setSearch] = useState("");

    const [dateRange, setDateRange] = useState({
        from: "",
        to: "",
    });


    type Key = "date" | "department" | "site" | "status" | "from" | "to";

    const getAttendanceHistory = async (
        filters?: Partial<Record<Key, string>>
    ) => {
        const params = new URLSearchParams({
            ...filter,
            ...dateRange,
            ...filters,
            take: "200",
        }).toString();

        const response = await sendRequest(
            "GET",
            `/attendance?${params}`
        );

        const formatAttendanceHistory = (
            attendance: AttendanceHistory[]
        ) => {
            return attendance.map((attHist, idx) => ({
                ...attHist,
                sn: idx + 1,
                siteName: capitalizeWords(attHist?.site?.name ?? ""),
                siteAddres: attHist?.site?.address,
                clockIn: attHist.clockIn
                    ? formatTime(attHist.clockIn)
                    : "---",
                clockOut: attHist.clockOut
                    ? formatTime(attHist.clockOut)
                    : "---",
                createdAt: formatDate(attHist.createdAt),
            }));
        };

        setAttendanceHistory(
            formatAttendanceHistory(response.attendance ?? [])
        );

    };


    useEffect(() => {
        const getSites = async () => {
            const sites = await sendRequest("GET", "/sites");
            setSites(sites);
        }

        try {
            setLoading(true);
            getSites();
            getAttendanceHistory();
        } catch (err: any) {
            console.log(err);
        } finally {
            setLoading(false);
        }
    }, []);


    const getStatus = (row: AttendanceHistory) => {
        if (row.clockIn && row.clockOut) return "Completed";
        if (row.clockIn) return "Active";
        return "Absent";
    };

    return (
        <div className="min-h-screen bg-slate-50">
            <FullPageSpinner show={loading} />

            <div className="mx-auto w-full max-w-[1600px] px-4 py-6 sm:px-6 lg:px-8">
                {/* ================= HEADER ========================= */}
                <div className="flex items-center gap-3">

                    {/* Back */}
                    <button
                        onClick={() => navigate(-1)}
                        className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-gray-500 transition hover:bg-gray-100 hover:text-gray-900"
                    >
                        {FaArrowLeft({})}
                    </button>

                    {/* Title */}
                    <div className="min-w-0 flex-1">
                        <h1 className="truncate text-xl font-bold text-gray-800 sm:text-2xl mb-4">
                            Attendance Logs
                        </h1>
                    </div>
                </div>


                {/* Search & Filter */}
                <div className="relative w-full">
                    {/* Search Icon */}
                    <span className="pointer-events-none absolute left-4 top-1/2 z-10 -translate-y-1/2 text-gray-400">
                        {FaSearch({})}
                    </span>

                    {/* Search input / selected filters */}
                    <div className="flex min-h-[46px] w-full items-center gap-2 rounded-xl border border-gray-200 bg-white py-1.5 pl-11 pr-14 text-sm transition focus-within:border-indigo-500 focus-within:ring-2 focus-within:ring-indigo-100">

                        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-1.5">
                            {filter.date !== "" && (
                                <FilterChip
                                    label={getDateLabel(filter.date, dateRange)}
                                    onRemove={() => {
                                        setFilter(prev => ({ ...prev, date: "" }));
                                        getAttendanceHistory({ "date": "", from: "", to: "" });
                                    }
                                    }
                                />
                            )}

                            {filter.site !== "all" && (
                                <FilterChip
                                    label={filter.site}
                                    onRemove={() => {
                                        setFilter(prev => ({ ...prev, site: "all" }));
                                        getAttendanceHistory({ "site": "all" });
                                    }}
                                />
                            )}

                            {/* {filter.department !== "all" && (
                                <FilterChip
                                    label={filter.department}
                                    onRemove={() => {
                                        setFilter(prev => ({ ...prev, department: "all" }));
                                        getAttendanceHistory({ "date": "" });
                                    }}
                                />
                            )} */}
                            {/* 
                            {filter.status !== "all" && (
                                <FilterChip
                                    label={filter.status}
                                    onRemove={() =>
                                        setFilter(prev => ({ ...prev, status: "all" }))
                                    }
                                />
                            )} */}

                            <input
                                type="text"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder={
                                    filter.date === "today" &&
                                        filter.site === "all" &&
                                        filter.department === "all" &&
                                        filter.status === "all"
                                        ? "Search..."
                                        : "Search..."
                                }
                                className="min-w-[120px] flex-1 border-0 bg-transparent py-1 outline-none placeholder:text-gray-400"
                            />
                        </div>
                    </div>

                    {/* Filter Button */}
                    <button
                        type="button"
                        onClick={() => {
                            setShowFilters(prev => !prev);
                            setActiveFilterMenu(null);
                        }}
                        className={`absolute right-2 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-lg transition ${showFilters
                            ? "bg-indigo-50 text-indigo-600"
                            : "text-gray-500 hover:bg-gray-100 hover:text-indigo-600"
                            }`}
                        title="Filter"
                    >
                        {FaFilter({})}
                    </button>

                    {/* Filter Menu */}
                    {showFilters && (
                        <div className="absolute right-0 top-[calc(100%+8px)] z-50 w-72 overflow-visible rounded-xl border border-gray-200 bg-white p-2 shadow-xl">

                            {/* Date */}
                            <FilterMenuItem
                                label="Date"
                                value={getDateLabel(filter.date)}
                                open={activeFilterMenu === "date"}
                                onClick={() => {
                                    setActiveFilterMenu(
                                        activeFilterMenu === "date" ? null : "date"
                                    );
                                }}
                            />

                            {activeFilterMenu === "date" && (
                                <div className="mt-1 rounded-lg bg-gray-50 p-2">
                                    <FilterOption
                                        label="Today"
                                        active={filter.date === "today"}
                                        onClick={() => {
                                            setFilter(prev => ({ ...prev, date: "today" }));
                                            setDateRange({ from: "", to: "" });
                                            setActiveFilterMenu(null);
                                        }}
                                    />

                                    <FilterOption
                                        label="Yesterday"
                                        active={filter.date === "yesterday"}
                                        onClick={() => {
                                            setFilter(prev => ({
                                                ...prev,
                                                date: "yesterday",
                                            }));
                                            setDateRange({ from: "", to: "" });
                                            setActiveFilterMenu(null);

                                            getAttendanceHistory();
                                        }}
                                    />

                                    <FilterOption
                                        label="This Week"
                                        active={filter.date === "week"}
                                        onClick={() => {
                                            setFilter(prev => ({ ...prev, date: "week" }));
                                            setDateRange({ from: "", to: "" });
                                            setActiveFilterMenu(null);

                                            getAttendanceHistory();
                                        }}
                                    />

                                    <FilterOption
                                        label="This Month"
                                        active={filter.date === "month"}
                                        onClick={() => {
                                            setFilter(prev => ({ ...prev, date: "month" }));
                                            setDateRange({ from: "", to: "" });
                                            setActiveFilterMenu(null);

                                            getAttendanceHistory();
                                        }}
                                    />

                                    <FilterOption
                                        label="This Year"
                                        active={filter.date === "year"}
                                        onClick={() => {
                                            setFilter(prev => ({ ...prev, date: "year" }));
                                            setDateRange({ from: "", to: "" });
                                            setActiveFilterMenu(null);

                                            getAttendanceHistory();
                                        }}
                                    />

                                    {/* Custom Range */}
                                    <div className="mt-2 border-t border-gray-200 pt-2">
                                        <p className="mb-2 px-2 text-xs font-medium text-gray-500">
                                            Custom Range
                                        </p>

                                        <div className="space-y-2">
                                            <input
                                                type="date"
                                                value={dateRange.from}
                                                onChange={(e) => {
                                                    setDateRange(prev => ({
                                                        ...prev,
                                                        from: e.target.value,
                                                    }))

                                                }}
                                                className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm outline-none focus:border-indigo-500"
                                            />

                                            <input
                                                type="date"
                                                value={dateRange.to}
                                                onChange={(e) =>
                                                    setDateRange(prev => ({
                                                        ...prev,
                                                        to: e.target.value,
                                                    }))
                                                }
                                                className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm outline-none focus:border-indigo-500"
                                            />

                                            <button
                                                disabled={!dateRange.from || !dateRange.to}
                                                onClick={() => {
                                                    setFilter(prev => ({
                                                        ...prev,
                                                        date: "custom",
                                                    }));
                                                    setActiveFilterMenu(null);
                                                }}
                                                className="w-full rounded-lg bg-indigo-600 px-3 py-2 text-sm font-medium text-white transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
                                            >
                                                Apply Range
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Site */}
                            <FilterMenuItem
                                label="Site"
                                value={filter.site === "all" ? "All Sites" : filter.site}
                                open={activeFilterMenu === "site"}
                                onClick={() => {
                                    setActiveFilterMenu(
                                        activeFilterMenu === "site" ? null : "site"
                                    )
                                    getAttendanceHistory();
                                }}
                            />

                            {activeFilterMenu === "site" && (
                                <div className="mt-1 rounded-lg bg-gray-50 p-2">
                                    {[
                                        "all",
                                        ...sites.map(site => capitalizeFirst(site.name)),
                                    ].map(site => (
                                        <FilterOption
                                            key={site}
                                            label={site === "all" ? "All Sites" : site}
                                            active={filter.site === site}
                                            onClick={() => {
                                                setFilter(prev => ({
                                                    ...prev,
                                                    site,
                                                }));
                                                setActiveFilterMenu(null);
                                            }}
                                        />
                                    ))}
                                </div>
                            )}


                            {/* Clear */}
                            <div className="mt-3 flex gap-2 border-t border-gray-100 pt-3">
                                <button
                                    type="button"
                                    onClick={() => {
                                        // Apply filters
                                        setShowFilters(false);
                                        setActiveFilterMenu(null)
                                        getAttendanceHistory();
                                    }}
                                    className="flex-1 rounded-lg bg-indigo-600 px-3 py-2.5 text-sm font-medium text-white transition hover:bg-indigo-700"
                                >
                                    Apply
                                </button>

                                <button
                                    type="button"
                                    onClick={() => {
                                        setFilter({
                                            date: "",
                                            department: "all",
                                            site: "all",
                                            status: "all",
                                        });
                                        // setShowFilters(false);

                                        setDateRange({
                                            from: "",
                                            to: "",
                                        });


                                        setSearch("")
                                        setActiveFilterMenu(null);
                                    }}
                                    className="flex-1 rounded-lg bg-gray-100 px-3 py-2.5 text-sm font-medium text-gray-700 transition hover:bg-gray-200"
                                >
                                    Clear
                                </button>
                            </div>
                        </div>
                    )}
                </div>


                {/* ======================= ATTENDANCE TABLE =============================== */}
                <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white mt-6 shadow-sm">


                    {/* Table */}
                    {attendanceHistory.length > 0 ? (
                        <>
                            {/* Recent Attendance */}
                            <div className="rounded-2xl border border-gray-200 bg-white p-5 sm:p-6">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <h2 className="text-lg font-semibold text-gray-900">
                                            Attendance History
                                        </h2>

                                        <p className="mt-1 text-sm text-gray-500">
                                            Keep track of attendance records.
                                        </p>
                                    </div>


                                </div>

                                {/* Desktop */}
                                <div className="mt-5 hidden overflow-x-auto md:block">
                                    <table className="min-w-full">
                                        <thead>
                                            <tr className="border-b border-gray-100 text-left text-xs font-semibold uppercase tracking-wide text-gray-400">
                                                <th className="px-3 py-3">
                                                    S/N
                                                </th>

                                                <th className="px-3 py-3">
                                                    Date
                                                </th>

                                                <th className="px-3 py-3">
                                                    Clock In
                                                </th>

                                                <th className="px-3 py-3">
                                                    Clock Out
                                                </th>

                                                <th className="px-3 py-3">
                                                    Site
                                                </th>
                                            </tr>
                                        </thead>

                                        <tbody className="divide-y divide-gray-100">
                                            {attendanceHistory.map((item, idx: number) => (
                                                <tr key={item.id}>

                                                    <td className="px-3 py-4 text-sm font-medium text-gray-800">
                                                        {idx + 1}
                                                    </td>
                                                    <td className="px-3 py-4 text-sm font-medium text-gray-800">
                                                        {formatDate(item.createdAt)}
                                                    </td>

                                                    <td className="px-3 py-4 text-sm text-gray-600">
                                                        {item.clockIn}
                                                    </td>

                                                    <td className="px-3 py-4 text-sm text-gray-600">
                                                        {item.clockOut}
                                                    </td>

                                                    <td className="px-3 py-4 text-sm text-gray-600 capitalize">
                                                        {item?.site?.name}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>

                                {/* Mobile */}
                                <div className="mt-5 space-y-3 md:hidden">
                                    {attendanceHistory.map((item) => (
                                        <div
                                            key={item.id}
                                            className="rounded-xl border border-gray-100 p-4"
                                        >
                                            <div className="flex items-center justify-between">
                                                <p className="text-sm font-semibold text-gray-800">
                                                    {item.createdAt}
                                                </p>

                                                <span
                                                    className="text-sm font-semibold text-gray-800 capitalize"
                                                >
                                                    {item.site?.name}
                                                </span>
                                            </div>

                                            <div className="mt-3 grid grid-cols-2 gap-4">
                                                <div>
                                                    <p className="text-xs text-gray-400">
                                                        Clock In
                                                    </p>

                                                    <p className="mt-1 text-sm font-medium text-gray-700">
                                                        {item.clockIn}
                                                    </p>
                                                </div>

                                                <div>
                                                    <p className="text-xs text-gray-400">
                                                        Clock Out
                                                    </p>

                                                    <p className="mt-1 text-sm font-medium text-gray-700">
                                                        {item.clockOut}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </>
                    ) : (
                        <div className="px-6 py-20 text-center">
                            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-100 text-slate-400">
                                {FaHistory({
                                    className: "text-xl",
                                })}
                            </div>

                            <h3 className="mt-5 text-base font-semibold text-slate-900">
                                No attendance records
                            </h3>

                            <p className="mx-auto mt-2 max-w-sm text-sm leading-6 text-slate-500">
                                No attendance records match your current filters.
                                Try changing the date, site, department, or status.
                            </p>

                            <button
                                type="button"
                                onClick={() => {
                                    const defaultFilter = {
                                        date: "", department: "all", to: "",
                                        site: "all", status: "all", from: ""
                                    };
                                    setDateRange({
                                        from: "",
                                        to: "",
                                    });

                                    setFilter(defaultFilter);
                                    getAttendanceHistory(defaultFilter);

                                }}
                                className="mt-5 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm transition hover:bg-slate-50"
                            >
                                Clear filters
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>


    );
}